import type { DirectoryBrowseIndex } from '@tabel/shared/personnel';
import type { MeResponse } from '@tabel/shared/schemas/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, Check, GitCompareArrows, Info, Plus, Search, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  PageHeader,
  SidePanel,
  Tabs,
  inputCls,
  textareaCls,
} from '../../components/ui';
import { useSetCrumbs } from '../../lib/crumbs';
import {
  type DirectoryCase,
  type Envelope,
  type Group,
  type GroupPage,
  type Page,
  type Person,
  type Preview,
  capabilities,
  command,
  dateTime,
  directoryRequest,
  queryString,
  queueState,
} from '../../lib/personnel-api';
import { AccountSection } from './AccountSection';
import { CasePanel, JobState } from './CasePanel';
import { PeopleDirectory } from './PeopleDirectory';
import { PersonPanel } from './PersonPanel';
import { ReviewPanel } from './ReviewPanel';
import { attendanceQuery } from './attendance-filters';
import { DirectoryExport, ErrorNotice, Loading, NoResults, StateBadge, Table, caseReason } from './parts';

export function PersonnelPage({ me }: { me: MeResponse }) {
  useSetCrumbs([{ label: 'Персонал' }]);
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const caps = capabilities(me);
  const view =
    params.get('view') ??
    (caps.includes('personnel.read') ? 'people' : caps.includes('identity.review') ? 'review' : 'cases');
  const filter = params.get('filter') ?? 'manual';
  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? '';
  useEffect(() => setSearch(q), [q]);
  const personId = params.get('person');
  const groupId = params.get('group');
  const caseId = params.get('case');
  const [browseClock, setBrowseClock] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setBrowseClock(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const attendance = attendanceQuery(params, browseClock);
  const set = (entries: Record<string, string | null>, replace = false) =>
    setParams(
      (old) => {
        const next = new URLSearchParams(old);
        for (const [k, v] of Object.entries(entries)) v ? next.set(k, v) : next.delete(k);
        return next;
      },
      { replace },
    );
  const people = useQuery({
    queryKey: [
      'personnel',
      'browse',
      me.object.id,
      me.directoryCapabilities,
      attendance.passFromMs,
      attendance.passToMs,
    ],
    queryFn: () =>
      directoryRequest<DirectoryBrowseIndex>(
        `/api/personnel/browse${queryString({
          passFromMs: attendance.passFromMs === undefined ? undefined : String(attendance.passFromMs),
          passToMs: attendance.passToMs === undefined ? undefined : String(attendance.passToMs),
        })}`,
      ),
    enabled: view === 'people' && !attendance.error,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const groups = useQuery({
    queryKey: ['personnel', 'groups', filter, q, params.get('cursor')],
    queryFn: () =>
      q
        ? directoryRequest<GroupPage>('/api/personnel/review/search', 'POST', { query: q, filter })
        : directoryRequest<GroupPage>(
            `/api/personnel/review/groups${queryString({ filter, cursor: params.get('cursor') ?? undefined })}`,
          ),
    enabled: view === 'review',
    refetchInterval: (query) =>
      queueState(query.state.data?.data) === 'pending'
        ? Math.max(500, Math.min(5000, query.state.data?.data.readiness?.refreshAfterMs ?? 1000))
        : 60_000,
  });
  const cases = useQuery({
    queryKey: ['personnel', 'cases', params.get('cursor')],
    queryFn: () =>
      directoryRequest<Page<DirectoryCase>>(
        `/api/personnel/cases${queryString({ cursor: params.get('cursor') ?? undefined })}`,
      ),
    enabled: view === 'cases',
  });
  const [createRequestId, setCreateRequestId] = useState(() => crypto.randomUUID());
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [basis, setBasis] = useState('');
  const [createError, setCreateError] = useState<unknown>(null);
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [duplicates, setDuplicates] = useState<Person[]>([]);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['personnel'] });
  const reviewState = groups.error ? 'failed' : queueState(groups.data?.data);
  const pendingReason: Record<string, string> = {
    accepted_inputs_pending: 'Обрабатываются принятые сведения',
    candidate_build_pending: 'Формируются межплощадочные группы',
    candidate_set_too_broad: 'Объём сверки превышает предел одного задания',
  };
  const create = async () => {
    setCreatingBusy(true);
    setCreateError(null);
    try {
      const result = await directoryRequest<Person>(
        '/api/personnel/people',
        'POST',
        command({ displayName: name.trim(), basis: basis.trim() }, [], undefined, createRequestId),
      );
      setCreating(false);
      set({ person: result.data.id });
      refresh();
    } catch (e) {
      setCreateError(e);
    } finally {
      setCreatingBusy(false);
    }
  };
  const canViewCases = caps.some((c) =>
    [
      'cases.manage',
      'source_mapping.manage',
      'identity.correct',
      'accounts.person_bind',
      'membership.manage',
    ].includes(c),
  );
  const canView = caps.includes('personnel.read') || caps.includes('identity.review') || canViewCases;
  const current = view === 'review' ? groups : view === 'cases' ? cases : people;
  const tabs = [
    ...(caps.includes('personnel.read') ? [{ value: 'people', label: 'Люди' }] : []),
    ...(caps.includes('identity.review') ? [{ value: 'review', label: 'Сверка между площадками' }] : []),
    ...(canViewCases ? [{ value: 'cases', label: 'Случаи справочника' }] : []),
  ];
  return (
    <div className="space-y-6">
      <PageHeader
        title="Персонал"
        description="Люди компании и их регистрации на площадках."
        actions={
          caps.includes('personnel.create') ? (
            <Button icon={Plus} variant="primary" onClick={() => setCreating(true)}>
              Добавить человека
            </Button>
          ) : undefined
        }
      />
      {!canView ? (
        <Callout icon={AlertCircle} tone="warn">
          Для этого раздела требуется отдельное право справочника. Обратитесь к администратору.
        </Callout>
      ) : (
        <>
          <Tabs
            label="Разделы персонала"
            idPrefix="personnel"
            value={view}
            onChange={(v) => set({ view: v, cursor: null, group: null, person: null, case: null })}
            items={tabs}
          />
          <div
            role="tabpanel"
            id={`personnel-panel-${view}`}
            aria-labelledby={`personnel-tab-${view}`}
            className="space-y-4"
          >
            {view === 'review' && (
              <form
                className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-end [&_label]:mb-0"
                onSubmit={(e) => {
                  e.preventDefault();
                  set({ q: search, cursor: null });
                }}
              >
                <div className="min-w-0 flex-1">
                  <Field label={view === 'review' ? 'Найти в допустимых группах' : 'Найти человека'}>
                    <input
                      aria-label={view === 'review' ? 'Найти в допустимых группах' : 'Найти человека'}
                      className={inputCls}
                      placeholder="Фамилия, имя или Company ID"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Field>
                </div>
                {view === 'review' ? (
                  <Field label="Показывать">
                    <select
                      aria-label="Показывать"
                      className={inputCls}
                      value={filter}
                      onChange={(e) => set({ filter: e.target.value, cursor: null })}
                    >
                      <option value="manual">Требуют решения</option>
                      <option value="all">Все группы</option>
                      <option value="automatic">Сопоставлены автоматически</option>
                      <option value="conflicts">Только спорные</option>
                      <option value="deferred">Отложенные</option>
                      <option value="reviewed">Рассмотренные</option>
                    </select>
                  </Field>
                ) : (
                  <Field label="Состояние">
                    <select
                      aria-label="Состояние"
                      className={inputCls}
                      value={params.get('state') ?? ''}
                      onChange={(e) => set({ state: e.target.value, cursor: null })}
                    >
                      <option value="">Все карточки</option>
                      <option value="provisional">Предварительные</option>
                      <option value="confirmed">Подтверждённые</option>
                    </select>
                  </Field>
                )}
                <Button type="submit" icon={Search} className="sm:mb-0">
                  Найти
                </Button>
              </form>
            )}
            {view === 'review' && groups.data && reviewState === 'ready' && (
              <Callout icon={Info}>
                <p>
                  Окно: последние 30 дней, по {dateTime(groups.data.data.asOfMs)}. Только регистрации с
                  проходами на разных площадках; уволенные исключены.
                </p>
                <p className="mt-1">
                  Однозначные совпадения ФИО сопоставляются автоматически. В разделе «Требуют решения»
                  показаны только спорные случаи; автоматические сопоставления доступны в отдельном фильтре.
                </p>
                <p className="mt-1">
                  Версия алгоритма: {groups.data.data.matcherVersion}.{' '}
                  {groups.error
                    ? 'Показан прежний снимок; обновите данные.'
                    : groups.data.data.freshness.state === 'fresh'
                      ? 'Данные актуальны.'
                      : groups.data.data.freshness.state === 'failed'
                        ? 'Обновление завершилось ошибкой.'
                        : 'Очередь обновляется.'}
                </p>
              </Callout>
            )}
            <ErrorNotice error={current.error} retry={() => void current.refetch()} />
            {current.isLoading && view !== 'people' ? (
              <Loading />
            ) : view === 'people' ? (
              <PeopleDirectory
                index={people.data?.data}
                params={params}
                set={set}
                fetching={people.isFetching}
                attendance={attendance}
                onOpen={(id) => set({ person: id })}
              />
            ) : view === 'review' && reviewState !== 'ready' ? (
              <Card>
                <div className="space-y-3 p-5">
                  <div role="status" aria-live="polite">
                    <h2 className="text-sm font-semibold">
                      {reviewState === 'pending' ? 'Сверка формируется' : 'Не удалось сформировать сверку'}
                    </h2>
                  </div>
                  <p className="text-sm text-ink-2">
                    {pendingReason[groups.data?.data.readiness?.reasonCode ?? ''] ??
                      (reviewState === 'pending'
                        ? 'Ожидается обработка принятых данных и построение групп.'
                        : 'Проверьте состояние обработки и повторите обновление.')}
                  </p>
                  <p className="text-sm text-ink-2">
                    Готовый результат ещё не получен. Сопоставление по этой очереди временно недоступно.
                  </p>
                  {reviewState === 'pending' ? (
                    <p className="text-xs text-ink-3">Страница обновится автоматически.</p>
                  ) : (
                    <Button onClick={() => void groups.refetch()}>Обновить состояние</Button>
                  )}
                  {reviewState === 'failed' &&
                    caps.includes('identity.correct') &&
                    caps.includes('personnel.read') && (
                      <Button onClick={() => set({ view: 'people', group: null, cursor: null })}>
                        Найти карточку для явного разбора
                      </Button>
                    )}
                  {reviewState === 'failed' &&
                    caps.includes('jobs.retry') &&
                    groups.data?.data.readiness?.jobId && (
                      <JobState
                        id={groups.data.data.readiness.jobId}
                        canRetry
                        onChanged={() => void groups.refetch()}
                      />
                    )}
                </div>
              </Card>
            ) : view === 'review' ? (
              <>
                <Table label="Группы сверки">
                  <thead>
                    <tr>
                      <th>Кандидаты</th>
                      <th>Регистрации</th>
                      <th>Состояние</th>
                      <th>
                        <span className="sr-only">Действия</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.data?.data.items.map((g) => (
                      <tr key={g.id}>
                        <td
                          data-label="Кандидаты"
                          className="sticky max-sm:min-w-0 max-sm:pr-32 max-sm:whitespace-normal"
                        >
                          <button
                            type="button"
                            className="text-left font-medium hover:text-accent"
                            onClick={() => set({ group: g.id })}
                          >
                            {g.displayName ?? 'Имя не получено'}
                          </button>
                        </td>
                        <td data-label="Регистрации">
                          {g.registrations.length} · {new Set(g.registrations.map((r) => r.siteId)).size}{' '}
                          площадки
                        </td>
                        <td data-label="Состояние">
                          <StateBadge state={g.state} />
                          {g.reviewReason && (
                            <p className="mt-1 max-w-sm text-xs text-ink-2">{caseReason(g.reviewReason)}</p>
                          )}
                        </td>
                        <td className="act">
                          <Button size="sm" icon={GitCompareArrows} onClick={() => set({ group: g.id })}>
                            Сравнить
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {!groups.data?.data.items.length && !groups.error && (
                  <NoResults title={filter === 'manual' ? 'Спорных случаев нет' : undefined}>
                    {filter === 'manual'
                      ? 'Автоматические сопоставления доступны в фильтре «Сопоставлены автоматически».'
                      : 'В этом виде нет доступных групп. Явный разбор прежних регистраций открывается из карточки человека.'}
                  </NoResults>
                )}
              </>
            ) : (
              <>
                <Table label="Случаи справочника">
                  <thead>
                    <tr>
                      <th>Причина</th>
                      <th>Состояние</th>
                      <th>Ответственный</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {cases.data?.data.items.map((c) => (
                      <tr key={c.id}>
                        <td
                          data-label="Причина"
                          className="sticky max-sm:min-w-0 max-sm:pr-24 max-sm:whitespace-normal"
                        >
                          {caseReason(c.reasonCode, c.message)}
                        </td>
                        <td data-label="Состояние">
                          <StateBadge state={c.state} />
                        </td>
                        <td data-label="Ответственный">{c.assigneeUserId ?? 'Не назначен'}</td>
                        <td className="act">
                          <Button size="sm" onClick={() => set({ case: c.id })}>
                            Разобрать
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {!cases.data?.data.items.length && !cases.error && (
                  <NoResults>Открытых случаев нет.</NoResults>
                )}
              </>
            )}
            {view !== 'people' &&
              (view === 'review' ? groups.data?.data.nextCursor : cases.data?.data.nextCursor) && (
                <Button
                  onClick={() =>
                    set({
                      cursor:
                        (view === 'review' ? groups.data?.data.nextCursor : cases.data?.data.nextCursor) ??
                        null,
                    })
                  }
                >
                  Следующая страница
                </Button>
              )}
            {view !== 'people' && params.get('cursor') && (
              <Button onClick={() => set({ cursor: null })}>К началу списка</Button>
            )}
          </div>
        </>
      )}
      {view === 'people' && caps.includes('directory.export') && people.data && (
        <DirectoryExport
          kind="people"
          label="Экспорт всего справочника"
          filters={{}}
          snapshotRevision={people.data.data.snapshotRevision}
        />
      )}
      {personId && (
        <PersonPanel
          key={personId}
          id={personId}
          caps={caps}
          onClose={() => set({ person: null }, true)}
          onChanged={refresh}
        />
      )}
      {groupId && (
        <ReviewPanel
          key={groupId}
          id={groupId}
          queueRevision={groups.data?.data.snapshotRevision}
          validThroughMs={groups.data?.data.validThroughMs}
          queueReady={reviewState === 'ready'}
          canCorrect={caps.includes('identity.correct')}
          onClose={() => set({ group: null }, true)}
          onChanged={() => {
            set({ group: null }, true);
            refresh();
          }}
        />
      )}
      {caseId && (
        <CasePanel
          key={caseId}
          id={caseId}
          caps={caps}
          onClose={() => set({ case: null }, true)}
          onChanged={refresh}
        />
      )}
      <SidePanel
        open={creating}
        onClose={() => setCreating(false)}
        label="Добавить человека"
        title="Новая карточка человека"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setCreating(false)}>Отмена</Button>
            <Button
              variant="primary"
              disabled={!searched || !name.trim() || !basis.trim() || creatingBusy}
              onClick={() => void create()}
            >
              {creatingBusy ? 'Сохранение…' : 'Создать карточку'}
            </Button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Field label="Фамилия, имя, отчество">
            <input
              aria-label="Фамилия, имя, отчество"
              className={inputCls}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSearched(false);
                setCreateRequestId(crypto.randomUUID());
              }}
            />
          </Field>
          <Button
            disabled={!name.trim()}
            icon={Search}
            onClick={async () => {
              try {
                const r = await directoryRequest<Page<Person>>('/api/personnel/people/search', 'POST', {
                  query: name.trim(),
                  purpose: 'card',
                });
                setDuplicates(r.data.items);
                setSearched(true);
              } catch (e) {
                setCreateError(e);
              }
            }}
          >
            Проверить существующие карточки
          </Button>
          {searched && (
            <Card>
              <div className="p-4 text-sm">
                {duplicates.length ? (
                  <>
                    <p className="mb-2 text-ink-2">Возможные совпадения — откройте и проверьте:</p>
                    {duplicates.map((p) => (
                      <Button
                        key={p.id}
                        variant="ghost"
                        onClick={() => {
                          setCreating(false);
                          set({ person: p.id });
                        }}
                      >
                        {p.displayName ?? p.id}
                      </Button>
                    ))}
                  </>
                ) : (
                  'Похожих карточек не найдено. Можно создать новую.'
                )}
              </div>
            </Card>
          )}
          <Field label="Основание создания">
            <textarea
              aria-label="Основание создания"
              className={textareaCls}
              value={basis}
              onChange={(e) => {
                setBasis(e.target.value);
                setCreateRequestId(crypto.randomUUID());
              }}
            />
          </Field>
          <ErrorNotice error={createError} />
        </form>
      </SidePanel>
    </div>
  );
}
